import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip
} from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import React, { useCallback, useEffect, useMemo } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useListProductImages from "@/features/line-sheet-sets/hooks/use-list-product-images";
import AppIcon from "@/features/ui/app-icon";
import AppButton from "@/features/line-sheet-sets/app-button";
import useReviewLineSheetProductImage from "@/features/line-sheet-sets/hooks/use-review-line-sheet-product-image";
import useApproveTempImage from "@/features/line-sheet-sets/hooks/use-approve-temp-image";
import useAddProductImage from "@/features/line-sheet-sets/hooks/use-add-product-image";
import AppLabel from "@/features/ui/app-label";
import ProductImageLabel from "@/features/order-sheet-sets/product-image-label";

export interface ModifyImageModalProps {
  productId: number;
  searchWord: string;
  sku?: string;
  brandName?: string;
  color?: string;
  isOpen: boolean;
  onClose: () => void;
  mutate: () => void;
}

export default function ModifyImageModal({
  productId,
  searchWord,
  sku,
  brandName,
  color,
  isOpen,
  onClose,
  mutate,
}: ModifyImageModalProps) {
  const { t, tTitle } = useI18nHelper();
  const { error: showError } = useAppToasts();

  const listProductImagesKey = useMemo(() => {
    return {
      request: {
        lineSheetProductId: productId,
        pageSize: 1,
        pageNumber: 1,
      },
    };
  }, [productId]);

  const {
    isLoading: isListProductImagesLoading,
    data: listProductImagesData,
    mutate: mutateListProductImagesData,
    error: listProductImagesError,
  } = useListProductImages(
    listProductImagesKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    fire: fireReviewLineSheetProductImage,
    error: reviewLineSheetProductImageError,
    isLoading: isReviewLineSheetProductImageLoading,
    clear: clearReviewLineSheetProductImage,
  } = useReviewLineSheetProductImage();

  const {
    fire: fireApproveTempImage,
    error: approveTempImageError,
    isLoading: isApproveTempImageLoading,
    clear: clearApproveTempImage,
  } = useApproveTempImage();

  const {
    fire: fireAddProductImage,
    error: addProductImageError,
    isLoading: isAddProductImageLoading,
    clear: clearAddProductImage,
  } = useAddProductImage();

  useEffect(() => {
    showError(listProductImagesError);
  }, [showError, listProductImagesError]);

  useEffect(() => {
    showError(reviewLineSheetProductImageError);
  }, [showError, reviewLineSheetProductImageError]);

  useEffect(() => {
    showError(approveTempImageError);
  }, [showError, approveTempImageError]);

  useEffect(() => {
    showError(addProductImageError);
  }, [showError, addProductImageError]);

  const images = useMemo(() => {
    const productImages = listProductImagesData?.productImages;
    if (!productImages || productImages.length === 0) {
      return [];
    }
    return productImages[0].images
      .filter((item) => {
        return item.type !== "LINE_SHEET_SAME_SKU_PRODUCT_IMAGE";
      })
      .map((item) => {
        return {
          ...item,
          isSelected:
            item.type === "LINE_SHEET_PRODUCT_IMAGE" &&
            ["APPROVED", "PENDING"].includes(item.reviewStatus),
        };
      });
  }, [listProductImagesData]);

  const [searchValue, setSearchValue] = React.useState<string>(searchWord);
  const [imageUrlValue, setImageUrlValue] = React.useState<string>("");

  // 승인/반려가 진행 중일 때 또 누르면 같은 이미지에 상반된 요청이 겹친다.
  const isMutating =
    isReviewLineSheetProductImageLoading ||
    isApproveTempImageLoading ||
    isAddProductImageLoading;

  const refresh = useCallback(() => {
    mutateListProductImagesData();
    mutate();
  }, [mutateListProductImagesData, mutate]);

  const handleImageClick = useCallback(
    (item: (typeof images)[number]) => {
      if (isMutating) {
        return;
      }

      if (item.isSelected) {
        fireReviewLineSheetProductImage(
          { lineSheetProductImageId: item.id },
          { reviewStatus: "REJECTED" }
        )
          .then(() => {
            clearReviewLineSheetProductImage();
            refresh();
          })
          .catch(() => {});
        return;
      }

      if (item.type === "TEMP_SEARCH_RESULT") {
        fireApproveTempImage(
          { tempImageId: item.id },
          { productId: productId }
        )
          .then(() => {
            clearApproveTempImage();
            refresh();
          })
          .catch(() => {});
        return;
      }

      fireReviewLineSheetProductImage(
        { lineSheetProductImageId: item.id },
        { reviewStatus: "APPROVED" }
      )
        .then(() => {
          clearReviewLineSheetProductImage();
          refresh();
        })
        .catch(() => {});
    },
    [
      isMutating,
      productId,
      refresh,
      fireReviewLineSheetProductImage,
      clearReviewLineSheetProductImage,
      fireApproveTempImage,
      clearApproveTempImage,
    ]
  );

  const trimmedImageUrl = imageUrlValue.trim();
  const isImageUrlValid = /^https?:\/\/\S+$/.test(trimmedImageUrl);

  const handleAddImageUrl = useCallback(() => {
    if (!isImageUrlValid || isMutating) {
      return;
    }
    fireAddProductImage({ productId }, { imageUrl: trimmedImageUrl })
      .then(() => {
        clearAddProductImage();
        setImageUrlValue("");
        refresh();
      })
      .catch(() => {});
  }, [
    isImageUrlValid,
    isMutating,
    productId,
    trimmedImageUrl,
    refresh,
    fireAddProductImage,
    clearAddProductImage,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"2xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          color={"#444440"}
          fontSize={"14px"}
          background={"#F7F9FA"}
          borderBottom={"0.5px solid #8F8F8C"}
          borderRadius={"8px 8px 0px 0px"}
        >
          {tTitle("image_add_modify")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box>
            <Grid templateColumns="repeat(3, 1fr)" rowGap="16px" columnGap="24px">
              <GridItem>
                <Text fontSize="sm" color="gray.500">Brand</Text>
                <Text fontWeight="bold">{brandName}</Text>
              </GridItem>
              <GridItem>
                <Text fontSize="sm" color="gray.500">SKU</Text>
                <Text fontWeight="bold">{sku}</Text>
              </GridItem>
              <GridItem>
                <Text fontSize="sm" color="gray.500">Color</Text>
                <Text fontWeight="bold">{color}</Text>
              </GridItem>
            </Grid>
          </Box>
          <Divider marginY="16px" borderColor="gray.400" />
          <Flex width="100%" gap="8px" marginBottom={"8px"} alignItems={"center"}>
            <Input
              key={"modify_image_modal_input"}
              flex={"0 0 35%"}
              placeholder={t("sku")}
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
            />
            <AppButton
              variant={"normal"}
              size={"large"}
              width={"100%"}
              chakraProps={{ width: "280px", height: "40px" }}
              onClick={() => {
                const url = `https://search.shopping.naver.com/search/all?where=all&frm=NVSCTAB&query=${encodeURIComponent(
                  searchValue
                )}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              <Text fontSize={"14px"}>
                {"네이버 직접 검색"}
              </Text>
            </AppButton>
            <AppButton
              variant={"normal"}
              size={"large"}
              width={"100%"}
              chakraProps={{ width: "280px", height: "40px" }}
              onClick={() => {
                const url = `https://www.google.com/search?q=${encodeURIComponent(
                  searchValue
                )}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              <Text fontSize={"14px"}>
                {"구글 직접 검색"}
              </Text>
            </AppButton>
          </Flex>
          <Flex width="100%" gap="8px" marginBottom={"16px"} alignItems={"center"}>
            <Input
              key={"modify_image_modal_image_url_input"}
              flex={"1"}
              placeholder={"이미지 URL을 붙여넣어 주세요 (https://...)"}
              value={imageUrlValue}
              isDisabled={isAddProductImageLoading}
              onChange={(event) => {
                setImageUrlValue(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleAddImageUrl();
                }
              }}
            />
            <AppButton
              variant={"primary"}
              size={"large"}
              isLoading={isAddProductImageLoading}
              isDisabled={!isImageUrlValid || isMutating}
              disabledMessage={"http:// 또는 https:// 로 시작하는 URL을 입력해 주세요."}
              chakraProps={{ width: "120px", height: "40px", flexShrink: 0 }}
              onClick={handleAddImageUrl}
            >
              <Text fontSize={"14px"}>
                {"이미지 추가"}
              </Text>
            </AppButton>
          </Flex>
          {isListProductImagesLoading && (
            <Flex
              width="100%"
              height="200px"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner m={"0 auto"} />
            </Flex>
          )}
          {!isListProductImagesLoading && (
            <Box position={"relative"} opacity={isMutating ? 0.5 : 1}>
              {isMutating && (
                <Flex
                  position={"absolute"}
                  top={"0"}
                  left={"0"}
                  width={"100%"}
                  height={"100%"}
                  justifyContent={"center"}
                  alignItems={"center"}
                  zIndex={"30"}
                >
                  <Spinner />
                </Flex>
              )}
              <SimpleGrid columns={[2, 3, 4, 5, 6, 7]} spacing="8px">
                {images.map((item) => {
                  const isOriginal = item.isOriginal;
                  return <Tooltip
                    key={`${item.type}_${item.id}`}
                    placement={"right"}
                    backgroundColor={`var(--gray-dark, #444440)`}
                    boxShadow={"0px 4px 8px 0px rgba(0, 0, 0, 0.20)"}
                    maxWidth={"auto"}
                    hasArrow={true}
                    p={"0"}
                    m={"0"}
                    label={<ProductImageLabel value={{
                      url: item.url,
                      type: isOriginal ? "ORIGINAL" : "ALTERNATIVE",
                    }} />}
                    modifiers={[
                      {
                        name: "preventOverflow",
                        options: {
                          padding: 8,
                        },
                      },
                    ]}
                  >
                    <Box
                      position="relative"
                      boxSize="74px"
                      onClick={() => handleImageClick(item)}
                      cursor={isMutating ? "wait" : "pointer"}
                    >
                      <Image
                        borderRadius={"8px"}
                        border={!item.isSelected ? "0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)" : "0.5px solid var(--Pathrade-Gray-Groomy, #1272EF)"}
                        background={"#fff"}
                        position={"relative"}
                        flexGrow={"1"}
                        alt={"alt"}
                        src={item.url}
                        boxSize={"74px"}
                        objectFit={"contain"}
                        zIndex={"10"}
                      />
                      <Box position={"absolute"} top={"4px"} left={"4px"} zIndex={"11"}>
                        <AppLabel
                          isToUpper={false}
                          size={"small"}
                          nameKey={isOriginal ? "원본" : "AI"}
                          level={isOriginal ? "LIGHT" : "SUCCESS"}
                          isFaded={isOriginal}
                        />
                      </Box>
                      {item.isSelected && (
                        <Box
                          position="absolute"
                          top="0"
                          left="0"
                          width="100%"
                          height="100%"
                          background="#1272EF1A"  // 반투명 블루 오버레이
                          borderRadius="8px"
                          zIndex="20"
                        />
                      )}
                      {item.isSelected && <Box position={"absolute"} bottom={"4px"} right={"4px"} zIndex={"11"}>
                        <AppIcon
                          icon={"check-circle"}
                          size={"medium"}
                        />
                      </Box>}
                    </Box>
                  </Tooltip>;
                })}
              </SimpleGrid>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
